import React, { useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Typography,
    CircularProgress,
    Box,
    Grid,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import BoltIcon from '@mui/icons-material/Bolt';
import PercentIcon from '@mui/icons-material/Percent';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '@mui/material/styles';
import { getVisitsForHouse, createVisit, updateVisit, deleteVisit } from '../../api_calls/api_calls_visits';
import { getHousePeriodicConsumptions } from '../../api_calls/api_calls_houses';
import VisitEditModal from './VisitEditModal';
import VisitCreateModal from './VisitCreateModal';

const toDate = (value) => {
    const d = value ? new Date(value) : null;
    return d && !isNaN(d.getTime()) ? d : null;
};

const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const endOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
};

const dateToKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const toApiDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const shortDate = (date, locale) => date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });

const getMonthGrid = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const firstWeekday = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(year, month, day));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
};

const getDateRange = (start, end) => {
    const from = startOfDay(start);
    const to = startOfDay(end);
    const out = [];
    const cursor = new Date(from);
    while (cursor <= to) {
        out.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return out;
};

const toDateTimeLocalInput = (value) => {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const removeGreekTonos = (text) => {
    if (!text) return text;
    return text
        .replace(/ά/g, 'α')
        .replace(/έ/g, 'ε')
        .replace(/ή/g, 'η')
        .replace(/ί/g, 'ι')
        .replace(/ό/g, 'ο')
        .replace(/ύ/g, 'υ')
        .replace(/ώ/g, 'ω')
        .replace(/Ά/g, 'Α')
        .replace(/Έ/g, 'Ε')
        .replace(/Ή/g, 'Η')
        .replace(/Ί/g, 'Ι')
        .replace(/Ό/g, 'Ο')
        .replace(/Ύ/g, 'Υ')
        .replace(/Ώ/g, 'Ω')
        .replace(/ϊ/g, 'ι')
        .replace(/ΐ/g, 'ι')
        .replace(/ϋ/g, 'υ')
        .replace(/ΰ/g, 'υ')
        .replace(/Ϊ/g, 'Ι')
        .replace(/Ϋ/g, 'Υ');
};

const VisitsManagement = ({ dictionary, houseId }) => {
    const theme = useTheme();
    const [visits, setVisits] = useState([]);
    const [selectedVisitId, setSelectedVisitId] = useState(null);
    const [selectedVisitConsumption, setSelectedVisitConsumption] = useState(null);
    const [consumptionLoading, setConsumptionLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        start_datetime: '',
        end_datetime: '',
        actual_check_in_datetime: '',
        actual_check_out_datetime: '',
        platform: '',
        ical_uid: '',
        houses_id: '',
        info: '',
    });
    const [submittingAction, setSubmittingAction] = useState(false);
    const [createForm, setCreateForm] = useState({
        start_datetime: '',
        end_datetime: '',
        platform: '',
        ical_uid: '',
        info: '',
    });
    const today = useMemo(() => startOfDay(new Date()), []);
    const currentMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
    const [visibleMonthDate, setVisibleMonthDate] = useState(currentMonthDate);
    const isGreek = useMemo(() => {
        const probe = `${dictionary?.visitsManagementTab || ''} ${dictionary?.occupancyRateThisMonth || ''}`;
        return /[Α-Ωα-ω]/.test(probe);
    }, [dictionary]);
    const locale = isGreek ? 'el-GR' : 'en-US';

    useEffect(() => {
        if (!houseId) {
            setVisits([]);
            setSelectedVisitId(null);
            return;
        }
        let mounted = true;
        setLoading(true);
        getVisitsForHouse(houseId)
            .then((data) => {
                if (!mounted) return;
                setVisits(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!mounted) return;
                setVisits([]);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, [houseId]);

    const parsedVisits = useMemo(() => {
        return visits
            .map((visit) => {
                const start = toDate(visit?.start_datetime);
                const end = toDate(visit?.end_datetime);
                if (!start || !end) return null;
                return {
                    ...visit,
                    startDate: startOfDay(start),
                    endDate: endOfDay(end),
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    }, [visits]);

    useEffect(() => {
        if (!parsedVisits.length) {
            setSelectedVisitId(null);
            return;
        }
        const hasSelected = parsedVisits.some((v) => v.id === selectedVisitId);
        if (hasSelected) return;
        const current = parsedVisits.find((visit) => today >= visit.startDate && today <= visit.endDate);
        const latestPast = [...parsedVisits]
            .reverse()
            .find((visit) => visit.endDate < today);
        const upcoming = parsedVisits.find((visit) => visit.startDate > today);
        const fallback = current || latestPast || upcoming || parsedVisits[0];
        setSelectedVisitId(fallback?.id ?? null);
    }, [parsedVisits, selectedVisitId, today]);

    const selectedVisit = useMemo(() => parsedVisits.find((v) => v.id === selectedVisitId) || null, [parsedVisits, selectedVisitId]);
    const isUpcomingSelected = !!selectedVisit && selectedVisit.startDate > today;
    const isCurrentSelected = !!selectedVisit && selectedVisit.startDate <= today && selectedVisit.endDate >= today;

    useEffect(() => {
        if (!selectedVisit?.startDate) return;
        setVisibleMonthDate(new Date(selectedVisit.startDate.getFullYear(), selectedVisit.startDate.getMonth(), 1));
    }, [selectedVisit?.id]);

    useEffect(() => {
        if (!houseId || !selectedVisit?.startDate || !selectedVisit?.endDate) {
            setSelectedVisitConsumption(null);
            return;
        }
        if (selectedVisit.startDate > today) {
            setSelectedVisitConsumption(null);
            setConsumptionLoading(false);
            return;
        }
        let mounted = true;
        setConsumptionLoading(true);
        const period = `period/${toApiDate(selectedVisit.startDate)}/${toApiDate(selectedVisit.endDate)}`;
        getHousePeriodicConsumptions(period, houseId)
            .then((response) => {
                if (!mounted) return;
                const rawLabels = Array.isArray(response?.labels) ? response.labels : [];
                const rawConsumption = Array.isArray(response?.consumption) ? response.consumption.map((v) => Number(v) || 0) : [];
                if (isCurrentSelected) {
                    const visitDays = getDateRange(selectedVisit.startDate, selectedVisit.endDate);
                    const labels = visitDays.map((day) => day.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }));
                    const todayIndex = Math.min(
                        visitDays.length - 1,
                        Math.max(0, Math.floor((startOfDay(today).getTime() - startOfDay(selectedVisit.startDate).getTime()) / (1000 * 60 * 60 * 24))),
                    );
                    const consumption = visitDays.map((_, index) => {
                        if (index > todayIndex) return 0;
                        return rawConsumption[index] ?? 0;
                    });
                    const futureMask = visitDays.map((_, index) => index > todayIndex);
                    setSelectedVisitConsumption({ labels, consumption, futureMask });
                    return;
                }
                setSelectedVisitConsumption({
                    labels: rawLabels,
                    consumption: rawConsumption,
                    futureMask: Array.from({ length: rawConsumption.length }, () => false),
                });
            })
            .catch(() => {
                if (!mounted) return;
                setSelectedVisitConsumption(null);
            })
            .finally(() => {
                if (mounted) setConsumptionLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, [houseId, selectedVisit, today, isCurrentSelected, locale]);

    const weekDays = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
        const monday = new Date(2024, 0, 1); // Monday
        return Array.from({ length: 7 }, (_, index) => {
            const d = new Date(monday);
            d.setDate(monday.getDate() + index);
            return formatter.format(d);
        });
    }, [locale]);
    const rawMonthName = visibleMonthDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    const monthName = isGreek ? removeGreekTonos(rawMonthName).toUpperCase() : rawMonthName.toUpperCase();
    const arrivalLabel = dictionary.arrival || 'Arrival';
    const departureLabel = dictionary.departure || 'Departure';
    const confirmedLabel = dictionary.confirmed || 'Confirmed';
    const occupancyBaseMonth = selectedVisit
        ? new Date(selectedVisit.startDate.getFullYear(), selectedVisit.startDate.getMonth(), 1)
        : currentMonthDate;
    const monthStart = new Date(occupancyBaseMonth.getFullYear(), occupancyBaseMonth.getMonth(), 1);
    const monthEnd = new Date(occupancyBaseMonth.getFullYear(), occupancyBaseMonth.getMonth() + 1, 0, 23, 59, 59, 999);
    const occupancyMonthLabel = occupancyBaseMonth.toLocaleDateString(locale, { month: 'long' });
    const upcomingCount = parsedVisits.filter((visit) => visit.startDate > today).length;
    const bookedDays = new Set();
    parsedVisits.forEach((visit) => {
        const overlapStart = visit.startDate > monthStart ? visit.startDate : monthStart;
        const overlapEnd = visit.endDate < monthEnd ? visit.endDate : monthEnd;
        if (overlapStart > overlapEnd) return;
        const cursor = startOfDay(overlapStart);
        const end = startOfDay(overlapEnd);
        while (cursor <= end) {
            bookedDays.add(dateToKey(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }
    });
    const daysInMonth = monthEnd.getDate();
    const occupancyRate = daysInMonth > 0 ? (bookedDays.size / daysInMonth) * 100 : 0;
    const totalConsumption = (selectedVisitConsumption?.consumption || []).reduce((sum, value) => sum + (Number(value) || 0), 0);
    const hasChartData = !!(selectedVisitConsumption?.labels?.length && selectedVisitConsumption?.consumption?.length);
    const upcomingVisits = parsedVisits.filter((visit) => visit.startDate > today);
    const currentVisit = parsedVisits.find((visit) => visit.startDate <= today && visit.endDate >= today) || null;
    const isCurrentlyOccupied = !!currentVisit;
    const nextStay = upcomingVisits[0] || null;
    const chartLabels = selectedVisitConsumption?.labels?.length ? selectedVisitConsumption.labels : [dictionary.noData || 'No data'];
    const chartConsumption = selectedVisitConsumption?.consumption?.length ? selectedVisitConsumption.consumption : [0];
    const chartFutureMask = selectedVisitConsumption?.futureMask?.length
        ? selectedVisitConsumption.futureMask
        : Array.from({ length: chartConsumption.length }, () => false);
    const hasFutureDays = chartFutureMask.some(Boolean);
    const yAxisMax = Math.max(1, ...chartConsumption.map((v) => Number(v) || 0));
    const chartMaxIndex = chartConsumption.length
        ? chartConsumption.reduce((best, val, i) => {
            if (chartFutureMask[i]) return best;
            if (chartFutureMask[best]) return i;
            return Number(chartConsumption[best]) < Number(val) ? i : best;
        }, 0)
        : -1;
    const occupancyChipLabelBase = isCurrentlyOccupied
        ? (dictionary.occupied || 'Occupied')
        : (dictionary.notOccupied || 'Available');
    const occupancyChipLabel = isGreek ? removeGreekTonos(occupancyChipLabelBase) : occupancyChipLabelBase;

    const chartOption = {
        xAxis: {
            type: 'category',
            data: chartLabels,
            axisLine: { show: true },
            axisLabel: { color: '#999', fontSize: 10 },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'value',
            max: yAxisMax,
            name: 'kWh',
            nameLocation: 'middle',
            nameGap: 38,
            nameTextStyle: { color: '#999', fontSize: 10 },
            axisLine: { show: false },
            axisLabel: { color: '#999', fontSize: 10 },
            splitLine: { lineStyle: { type: 'dashed', color: '#EEE' } },
        },
        series: [
            {
                type: 'bar',
                silent: true,
                tooltip: { show: false },
                data: chartConsumption.map((_, index) => (chartFutureMask[index] ? yAxisMax : 0)),
                itemStyle: {
                    color: theme.palette.grey[400],
                    opacity: 0.45,
                },
                barGap: '-100%',
                barWidth: chartLabels.length > 7 ? '70%' : '60%',
                z: 1,
            },
            {
                type: 'bar',
                data: chartConsumption.map((value, index) => ({
                    value: chartFutureMask[index] ? 0 : value,
                    itemStyle: {
                        color: index === chartMaxIndex ? theme.palette.primary.main : theme.palette.secondary.main,
                    },
                })),
                itemStyle: {
                    color: theme.palette.secondary.main,
                },
                barWidth: chartLabels.length > 7 ? '70%' : '60%',
                z: 2,
            },
        ],
        grid: { top: 28, bottom: 36, left: 52, right: 16 },
        tooltip: { trigger: 'axis', formatter: '{b}<br/>{c} kWh' },
    };

    const calendarCells = getMonthGrid(visibleMonthDate);
    const noVisitsMessage = dictionary.noVisitsPlanned || 'No visits are currently planned.';
    const openEditModal = () => {
        if (!selectedVisit) return;
        setEditForm({
            start_datetime: toDateTimeLocalInput(selectedVisit.start_datetime || selectedVisit.startDate),
            end_datetime: toDateTimeLocalInput(selectedVisit.end_datetime || selectedVisit.endDate),
            actual_check_in_datetime: toDateTimeLocalInput(selectedVisit.actual_check_in_datetime),
            actual_check_out_datetime: toDateTimeLocalInput(selectedVisit.actual_check_out_datetime),
            platform: selectedVisit.platform || '',
            ical_uid: selectedVisit.ical_uid || '',
            houses_id: selectedVisit.houses_id || String(houseId || ''),
            info: selectedVisit.info || '',
        });
        setEditOpen(true);
    };
    const openCreateModal = () => {
        setCreateForm({
            start_datetime: '',
            end_datetime: '',
            platform: '',
            ical_uid: '',
            info: '',
        });
        setCreateOpen(true);
    };
    const handleCreateVisit = async () => {
        if (!houseId || !createForm.start_datetime || !createForm.end_datetime || !createForm.platform) return;
        setSubmittingAction(true);
        try {
            await createVisit({
                start_datetime: new Date(createForm.start_datetime).toISOString(),
                end_datetime: new Date(createForm.end_datetime).toISOString(),
                ical_uid: createForm.ical_uid ? createForm.ical_uid : null,
                platform: createForm.platform,
                info: createForm.info || '',
                houses_id: String(houseId),
            });
            const refreshed = await getVisitsForHouse(houseId);
            setVisits(Array.isArray(refreshed) ? refreshed : []);
            setCreateOpen(false);
        } finally {
            setSubmittingAction(false);
        }
    };
    const handleSaveVisitEdit = async () => {
        if (!selectedVisit?.id || !editForm.start_datetime || !editForm.end_datetime) return;
        setSubmittingAction(true);
        try {
            const payload = {
                start_datetime: new Date(editForm.start_datetime).toISOString(),
                end_datetime: new Date(editForm.end_datetime).toISOString(),
                platform: editForm.platform || '',
                ical_uid: editForm.ical_uid || '',
                houses_id: editForm.houses_id || String(houseId || ''),
                info: editForm.info || '',
            };
            if (editForm.actual_check_in_datetime) payload.actual_check_in_datetime = new Date(editForm.actual_check_in_datetime).toISOString();
            if (editForm.actual_check_out_datetime) payload.actual_check_out_datetime = new Date(editForm.actual_check_out_datetime).toISOString();
            await updateVisit(selectedVisit.id, {
                ...payload,
            });
            const refreshed = await getVisitsForHouse(houseId);
            setVisits(Array.isArray(refreshed) ? refreshed : []);
            setEditOpen(false);
        } finally {
            setSubmittingAction(false);
        }
    };
    const handleDeleteSelectedVisit = async () => {
        if (!selectedVisit?.id) return;
        setSubmittingAction(true);
        try {
            await deleteVisit(selectedVisit.id);
            const refreshed = await getVisitsForHouse(houseId);
            setVisits(Array.isArray(refreshed) ? refreshed : []);
            setDeleteOpen(false);
        } finally {
            setSubmittingAction(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                    <CircularProgress size={26} />
                </Box>
            ) : parsedVisits.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {noVisitsMessage}
                </Typography>
            ) : (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                        <Chip
                            size="small"
                            label={occupancyChipLabel}
                            sx={{
                                height: 30,
                                borderRadius: 1.5,
                                fontWeight: 800,
                                bgcolor: isCurrentlyOccupied ? '#dcfce7' : '#f1f5f9',
                                color: isCurrentlyOccupied ? '#166534' : '#475569',
                                border: `1px solid ${isCurrentlyOccupied ? '#86efac' : '#cbd5e1'}`,
                                textTransform: 'uppercase',
                            }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                startIcon={<EditOutlinedIcon />}
                                onClick={openEditModal}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 1.75,
                                    borderColor: '#3b82f6',
                                    color: '#3b82f6',
                                    bgcolor: '#fff',
                                    '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' },
                                }}
                            >
                                {dictionary.editSelectedVisit || 'Edit selected visit'}
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => setDeleteOpen(true)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 1.75,
                                    boxShadow: 'none',
                                    '&:hover': { boxShadow: 'none' },
                                }}
                            >
                                {dictionary.deleteSelectedVisit || 'Delete selected visit'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openCreateModal}
                                sx={{
                                    textTransform: 'none',
                                    bgcolor: '#3b82f6',
                                    color: '#fff',
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: '#2563eb' },
                                }}
                            >
                                {dictionary.scheduleNewVisit || 'Schedule New Visit'}
                            </Button>
                        </Box>
                    </Box>

                    <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={12} lg={4} sx={{ display: 'flex', minHeight: { lg: 680 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                <Box sx={{ mb: 1.25 }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.75,
                                            borderRadius: 2,
                                            border: '1px solid #e2e8f0',
                                            borderLeft: '4px solid #2563eb',
                                            minHeight: 130,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            textAlign: 'left',
                                            position: 'relative',
                                        }}
                                    >
                                        <PercentIcon sx={{ position: 'absolute', top: 10, right: 10, fontSize: 24, color: '#2563eb' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.5 }}>
                                            {`${isGreek ? removeGreekTonos(dictionary.occupancyRateThisMonth || 'Occupancy rate this month') : (dictionary.occupancyRateThisMonth || 'Occupancy rate this month')} (${isGreek ? removeGreekTonos(occupancyMonthLabel) : occupancyMonthLabel})`}
                                        </Typography>
                                        <Typography variant="h4" fontWeight={700}>{occupancyRate.toFixed(0)}%</Typography>
                                    </Paper>
                                </Box>
                                <Box sx={{ mb: 1.25 }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.75,
                                            borderRadius: 2,
                                            border: '1px solid #e2e8f0',
                                            borderLeft: '4px solid #f59e0b',
                                            minHeight: 150,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            textAlign: 'left',
                                            position: 'relative',
                                        }}
                                    >
                                        <EventAvailableIcon sx={{ position: 'absolute', top: 10, right: 10, fontSize: 24, color: '#f59e0b' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: 0.5 }}>
                                            {isGreek ? 'ΕΠΟΜΕΝΕΣ ΔΙΑΜΟΝΕΣ' : (dictionary.upcomingStays || 'Upcoming stays')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
                                            <Typography variant="h4" fontWeight={700}>{upcomingCount}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                {confirmedLabel}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            {upcomingVisits.slice(0, 2).map((visit) => (
                                                <Box key={`upcoming-${visit.id}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 0.75 }}>
                                                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <PersonIcon sx={{ fontSize: 12, color: '#475569' }} />
                                                        </Box>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                        >
                                                            {visit.platform || dictionary.visit || 'Visit'}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        size="small"
                                                        label={shortDate(visit.startDate, locale)}
                                                        sx={{ height: 22, bgcolor: '#e2efff', color: '#2563eb', fontWeight: 700, '& .MuiChip-label': { px: 1 } }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Paper>
                                </Box>
                                <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            minHeight: 0,
                                            height: '100%',
                                            border: '1px solid #e2e8f0',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flex: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {dictionary.visitConsumptionBreakdown || 'Visit consumption breakdown'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                                    {dictionary.total || 'Total'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700 }}>
                                                    {`${totalConsumption.toFixed(2)} kWh`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {consumptionLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 0 }}>
                                                <CircularProgress size={30} />
                                            </Box>
                                        ) : !hasChartData ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 0 }}>
                                                <Typography variant="body2" textAlign="center" sx={{ color: '#94a3b8', fontWeight: 400 }}>
                                                    {dictionary.visitConsumptionAvailableAfterEnd || 'Consumption details will be available after the visit ends.'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                                <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
                                            </Box>
                                        )}
                                    </Paper>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} lg={8}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Button
                                        size="small"
                                        variant="text"
                                        sx={{ minWidth: 28, p: 0.5 }}
                                        onClick={() => setVisibleMonthDate(new Date(visibleMonthDate.getFullYear(), visibleMonthDate.getMonth() - 1, 1))}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </Button>
                                    <Typography variant="body2" fontWeight={700}>{monthName}</Typography>
                                    <Button
                                        size="small"
                                        variant="text"
                                        sx={{ minWidth: 28, p: 0.5 }}
                                        onClick={() => setVisibleMonthDate(new Date(visibleMonthDate.getFullYear(), visibleMonthDate.getMonth() + 1, 1))}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                                    {weekDays.map((d, idx) => (
                                        <Typography key={`${d}-${idx}`} variant="caption" color="text.secondary" textAlign="center">
                                            {d}
                                        </Typography>
                                    ))}
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                                    {calendarCells.map((cell, idx) => {
                                        if (!cell) return <Box key={`empty-${idx}`} sx={{ height: 74 }} />;
                                        const key = dateToKey(cell);
                                        const inAnyVisit = parsedVisits.some((visit) => cell >= visit.startDate && cell <= visit.endDate);
                                        const inSelected = selectedVisit ? cell >= selectedVisit.startDate && cell <= selectedVisit.endDate : false;
                                        const arrivalsToday = parsedVisits.filter((visit) => dateToKey(visit.startDate) === key);
                                        const departuresToday = parsedVisits.filter((visit) => dateToKey(visit.endDate) === key);
                                        const topArrival = arrivalsToday[0] || null;
                                        const topDeparture = departuresToday[0] || null;
                                        return (
                                            <Box
                                                key={key}
                                                sx={{
                                                    minHeight: 74,
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'space-between',
                                                    p: 0.5,
                                                    bgcolor: inSelected ? '#dbeafe' : inAnyVisit ? '#edf3ff' : '#ffffff',
                                                    border: inSelected ? '1px solid #93c5fd' : '1px solid #e5e7eb',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                                                    {cell.getDate()}
                                                </Typography>
                                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                    {topArrival && (
                                                        <Chip
                                                            size="small"
                                                            label={(
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                                                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'inherit' }}>
                                                                        {topArrival.platform || dictionary.visit || 'Visit'}
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'inherit', opacity: 0.9 }}>
                                                                        {arrivalLabel}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            onClick={() => {
                                                                setSelectedVisitId(topArrival.id);
                                                                setVisibleMonthDate(new Date(topArrival.startDate.getFullYear(), topArrival.startDate.getMonth(), 1));
                                                            }}
                                                            sx={{
                                                                maxWidth: '100%',
                                                                height: 28,
                                                                bgcolor: topArrival.id === selectedVisitId ? '#2563eb' : '#e2e8f0',
                                                                color: topArrival.id === selectedVisitId ? '#fff' : '#334155',
                                                                border: topArrival.id === selectedVisitId ? '1px solid #2563eb' : '1px solid #93c5fd',
                                                                fontWeight: 700,
                                                                cursor: 'pointer',
                                                                '& .MuiChip-label': {
                                                                    px: 0.75,
                                                                    py: 0.25,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                    {topDeparture && (
                                                        <Chip
                                                            size="small"
                                                            label={(
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                                                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'inherit' }}>
                                                                        {topDeparture.platform || dictionary.visit || 'Visit'}
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'inherit', opacity: 0.9 }}>
                                                                        {departureLabel}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            onClick={() => {
                                                                setSelectedVisitId(topDeparture.id);
                                                                setVisibleMonthDate(new Date(topDeparture.startDate.getFullYear(), topDeparture.startDate.getMonth(), 1));
                                                            }}
                                                            sx={{
                                                                maxWidth: '100%',
                                                                height: 28,
                                                                bgcolor: topDeparture.id === selectedVisitId ? '#2563eb' : '#e2e8f0',
                                                                color: topDeparture.id === selectedVisitId ? '#fff' : '#334155',
                                                                border: topDeparture.id === selectedVisitId ? '1px solid #2563eb' : '1px solid #93c5fd',
                                                                fontWeight: 700,
                                                                cursor: 'pointer',
                                                                '& .MuiChip-label': {
                                                                    px: 0.75,
                                                                    py: 0.25,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                    {!topArrival && !topDeparture && <Box sx={{ height: 20 }} />}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {dictionary.selectedVisitLegend || 'Selected visit'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.light' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {dictionary.otherVisitsLegend || 'Other visits'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {parsedVisits.map((visit) => {
                                        const isSelected = visit.id === selectedVisitId;
                                        return (
                                            <Chip
                                                key={visit.id}
                                                label={`${visit.platform || 'visit'} ${toApiDate(visit.startDate)}`}
                                                onClick={() => {
                                                    setSelectedVisitId(visit.id);
                                                    setVisibleMonthDate(new Date(visit.startDate.getFullYear(), visit.startDate.getMonth(), 1));
                                                }}
                                                clickable
                                                color={isSelected ? 'primary' : 'default'}
                                                size="small"
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        );
                                    })}
                                </Box>

                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            width: '100%',
                                            minHeight: 90,
                                            border: '1px solid #e2e8f0',
                                            bgcolor: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1.25,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <BoltIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: '#475569', letterSpacing: 0.5 }}>
                                                    {isGreek ? removeGreekTonos(dictionary.currentStayConsumption || 'Current stay total consumption') : (dictionary.currentStayConsumption || 'Current stay total consumption')}
                                                </Typography>
                                                <Typography variant="body1" fontWeight={800} sx={{ color: '#0f172a' }}>
                                                    {totalConsumption.toFixed(2)} kWh
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            width: '100%',
                                            minHeight: 90,
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 2,
                                            bgcolor: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1.25,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <EventAvailableIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: '#475569', letterSpacing: 0.5 }}>
                                                    {isGreek
                                                        ? removeGreekTonos(dictionary.nextStayDates || dictionary.nextStay || 'Next stay')
                                                        : (dictionary.nextStayDates || dictionary.nextStay || 'Next stay')}
                                                </Typography>
                                                <Typography variant="body1" fontWeight={800} sx={{ color: '#0f172a' }}>
                                                    {nextStay
                                                        ? nextStay.startDate.toLocaleDateString(locale, { day: '2-digit', month: 'long' })
                                                        : (dictionary.none || 'None')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            width: '100%',
                                            minHeight: 90,
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 2,
                                            bgcolor: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1.25,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <EventAvailableIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: '#475569', letterSpacing: 0.5 }}>
                                                    {isGreek ? removeGreekTonos(dictionary.upcomingStays || 'Upcoming stays') : (dictionary.upcomingStays || 'Upcoming stays')}
                                                </Typography>
                                                <Typography variant="body1" fontWeight={800} sx={{ color: '#0f172a' }}>
                                                    {upcomingCount}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            )}
            <VisitEditModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSave={handleSaveVisitEdit}
                submitting={submittingAction}
                editForm={editForm}
                setEditForm={setEditForm}
                dictionary={dictionary}
            />
            <VisitCreateModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreate={handleCreateVisit}
                submitting={submittingAction}
                createForm={createForm}
                setCreateForm={setCreateForm}
                dictionary={dictionary}
            />

            <Dialog open={deleteOpen} onClose={() => !submittingAction && setDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{dictionary.visitDeleteConfirmTitle || 'Delete confirmation'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {dictionary.visitDeleteConfirmMessage || 'Are you sure you want to delete the selected visit?'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteOpen(false)} disabled={submittingAction}>
                        {dictionary.visitActionCancel || 'Cancel'}
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDeleteSelectedVisit} disabled={submittingAction}>
                        {dictionary.visitActionDelete || 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default VisitsManagement;
